import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Styles from './styles.module.scss'

import Seo from "../../../components/_UI/seo";
import {useTranslation} from "react-i18next";

import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {getNotifications, readAllNotifications, readNotification} from "../../../store/cabinet/action";
import {
    notificationDateHandler,
    notificationFilterOptions,
    notificationTypesParam,
    notificationViewHandler
} from "../../../helpers/notifications";

const PER_PAGE = 20;

// значения сортировки — те же, что понимает API
const SORT_NEW = 'newest';
const SORT_OLD = 'oldest';

// Небольшой выпадающий список: в проекте своего селекта нет, а нативный
// в тёмной теме не стилизуется
const FilterSelect = ({label, value, options, onChange}) => {
    const [opened, setOpened] = useState(false);
    const wrapRef = useRef(null);

    useEffect(()=>{
        if (!opened){
            return;
        }

        const clickHandler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)){
                setOpened(false);
            }
        };

        document.addEventListener('mousedown', clickHandler);
        return ()=>{document.removeEventListener('mousedown', clickHandler)};
    },[opened])

    const current = options.find(item => item.value === value);

    return (
        <div className={`${Styles.filterCol}`}>
            <div className={`${Styles.filterLabel}`}>{label}</div>
            <div className={`${Styles.selectWrap}`} ref={wrapRef}>
                <div
                    className={`${Styles.selectValue} ${opened ? Styles.selectValueOpened : ''}`}
                    onClick={()=>{setOpened(!opened)}}
                >
                    <span>{current ? current.label : ''}</span>
                    <svg className={`${Styles.selectArrow}`} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="#36F6FF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                {opened ? (
                    <div className={`${Styles.selectList}`}>
                        {options.map(item => (
                            <div
                                key={'events-filter_' + label + '_' + item.value}
                                className={`${Styles.selectItem} ${item.value === value ? Styles.selectItemActive : ''}`}
                                onClick={()=>{onChange(item.value); setOpened(false)}}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const CabinetEventsPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const authStatus = useSelector(store => store.main.authorizationStatus);
    const notifications = useSelector(store => store.cabinet.notifications);
    const notificationsStarted = useSelector(store => store.cabinet.notificationsStarted);

    const [sort, setSort] = useState(SORT_NEW);
    const [type, setType] = useState('');

    const lang = i18n.language ? i18n.language.slice(0, 2) : '';

    useEffect(()=>{
        if (!authStatus){
            navigate('/sign-in');
        }
    },[authStatus, navigate])

    const list = useMemo(()=> notifications?.notifications || [], [notifications])
    const pagination = notifications?.pagination;
    const total = pagination?.total || 0;
    const hasMore = pagination ? pagination.current_page < pagination.last_page : false;

    // список типов приходит с сервера — так новые типы появятся в фильтре сами
    const availableTypes = notifications?.available_types;

    // одна категория макета может закрывать несколько типов API
    // (все статусы вывода — один чип «Вывод»)
    const typeParam = useMemo(
        ()=> notificationTypesParam(type, availableTypes),
        [type, availableTypes]
    );

    // фильтры и сортировка серверные: при смене тянем список заново с первой страницы
    useEffect(()=>{
        if (authStatus){
            dispatch(getNotifications({perPage: PER_PAGE, type: typeParam, sort}));
        }
    },[authStatus, dispatch, typeParam, sort])

    const sortOptions = useMemo(()=>([
        {value: SORT_NEW, label: t('Сначала новые')},
        {value: SORT_OLD, label: t('Сначала старые')}
    ]),[t])

    const typeOptions = useMemo(
        ()=> notificationFilterOptions(t, availableTypes),
        [t, availableTypes]
    );

    const moreHandler = useCallback(()=>{
        if (notificationsStarted || !hasMore){
            return;
        }
        dispatch(getNotifications({
            page: pagination.current_page + 1,
            perPage: PER_PAGE,
            type: typeParam,
            sort,
            append: true
        }));
    },[dispatch, notificationsStarted, hasMore, pagination, typeParam, sort])

    const readHandler = useCallback((item)=>{
        if (item?.read){
            return;
        }
        dispatch(readNotification(item?.id));
    },[dispatch])

    return (
        <section className={`${Styles.container}`}>
            <Seo title={t('Все события')}/>
            <div className={`${Styles.posWrap}`}>
                <div className={`${Styles.backBtn}`} onClick={()=>{navigate('/cabinet')}}>
                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 1L1 6L6 11M1 6H15" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t('Вернуться')}</span>
                </div>

                <div className={`${Styles.headWrap}`}>
                    <div className={`${Styles.headPos}`}>
                        <h1 className={`${Styles.headTitle}`}>{t('Все события')}</h1>
                        <div className={`${Styles.headNote}`}>
                            {t('Хронология событий вашего аккаунта и платформы')}
                        </div>
                    </div>

                    <div className={`${Styles.totalCard}`}>
                        <div className={`${Styles.totalValue}`}>{total}</div>
                        <div className={`${Styles.totalName}`}>{t('событий за всё время')}</div>
                    </div>
                </div>

                <div className={`${Styles.filtersWrap}`}>
                    <FilterSelect
                        label={t('Сортировка')}
                        value={sort}
                        options={sortOptions}
                        onChange={setSort}
                    />
                    <FilterSelect
                        label={t('Тип события')}
                        value={type}
                        options={typeOptions}
                        onChange={setType}
                    />

                    <div className={`${Styles.filtersSide}`}>
                        {notifications?.unread_count ? (
                            <div
                                className={`${Styles.readAllBtn}`}
                                onClick={()=>{dispatch(readAllNotifications())}}
                            >
                                {t('Прочитать все')}
                            </div>
                        ) : null}
                        <div className={`${Styles.shownNote}`}>
                            {t('Показано')} {list.length} {t('из')} {total}
                        </div>
                    </div>
                </div>

                <div className={`${Styles.listWrap}`}>
                    {list.length ? list.map((item, index)=>{
                        const date = notificationDateHandler(item?.created_at, lang);
                        const view = notificationViewHandler(item, t, lang);

                        return (
                            <React.Fragment key={'cabinet-event_' + item?.id}>
                                {index ? <div className={`${Styles.rowDivider}`}/> : null}
                                <div
                                    className={`${Styles.eventRow} ${item?.read ? '' : Styles.eventRowNew}`}
                                    onClick={()=>{readHandler(item)}}
                                >
                                    <div className={`${Styles.eventDate}`}>
                                        <span className={`${Styles.eventDay}`}>{date.day}</span>
                                        <span className={`${Styles.eventMonth}`}>{date.month}</span>
                                        <span className={`${Styles.eventTime}`}>{date.time}</span>
                                    </div>

                                    <div className={`${Styles.eventPos}`}>
                                        <div className={`${Styles.eventHead}`}>
                                            <span className={`${Styles.eventTitle}`}>{view.title}</span>
                                            <span className={`${Styles.eventChip} ${Styles['chip_' + view.category]}`}>
                                                <span className={`${Styles.eventChipDot}`}/>
                                                {view.categoryLabel}
                                            </span>
                                            {item?.read ? null : <span className={`${Styles.eventDot}`}/>}
                                        </div>
                                        {view.text ? (
                                            <div className={`${Styles.eventNote}`}>{view.text}</div>
                                        ) : null}
                                        {view.link ? (
                                            <a
                                                className={`${Styles.eventLink}`}
                                                href={view.link}
                                                target='_blank'
                                                rel='noreferrer'
                                                onClick={(e)=>{e.stopPropagation()}}
                                            >
                                                {t('Посмотреть транзакцию')}
                                            </a>
                                        ) : null}
                                    </div>

                                    {view.amount ? (
                                        <div className={`${Styles.eventAmount} ${view.amountSign === 'minus' ? Styles.eventAmountMinus : ''}`}>
                                            {view.amountSign === 'minus' ? '−' : '+'}{view.amount}
                                        </div>
                                    ) : null}
                                </div>
                            </React.Fragment>
                        );
                    }) : (
                        <div className={`${Styles.emptyNote}`}>
                            {notificationsStarted ? t('Загружаем…') : t('Событий пока нет')}
                        </div>
                    )}
                </div>

                {hasMore ? (
                    <div className={`${Styles.moreWrap}`}>
                        <div className={`${Styles.moreBtn}`} onClick={moreHandler}>
                            {notificationsStarted ? t('Загружаем…') : t('Показать ещё')}
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default CabinetEventsPage;
